/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react'; // oxlint-disable-line typescript-eslint/consistent-type-imports -- jsx used via classic JSX pragma

import { HeadingIcon } from '@af/design-system-docs-ui/heading-icon';
import CustomItem from '@atlaskit/menu/custom-item';
import type { CustomItemProps } from '@atlaskit/menu/types';
import { Anchor } from '@atlaskit/primitives/anchor';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { token } from '@atlaskit/tokens';

import { type Indices } from './client';

type HeadingItemProps = PropsWithChildren<{
	indexName: Indices;
}>;

const backgroundMapping = {
	Components: token('color.background.accent.green.subtlest'),
	Tokens: token('color.background.accent.yellow.subtlest'),
	Foundations: token('color.background.accent.teal.subtlest'),
	'Get started': token('color.background.accent.purple.subtlest'),
	Patterns: token('color.background.accent.orange.subtlest'),
};

const headingItemStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	paddingBlock: 'space.150',
	paddingInline: 'space.150',
	marginBlock: 'space.0',
	marginInline: 'space.200',
	borderRadius: 'radius.large',
	gap: 'space.100',
});

/**
 * __Heading item__
 */
export const HeadingItem = ({ children, indexName }: HeadingItemProps): jsx.JSX.Element => (
	<CustomItem
		component={({ children }) => (
			<Box
				xcss={headingItemStyles}
				style={{
					backgroundColor: backgroundMapping[indexName],
				}}
			>
				<HeadingIcon heading={indexName} />
				{children}
			</Box>
		)}
	>
		{children}
	</CustomItem>
);

interface LinkItemProps extends Omit<CustomItemProps, 'onClick'> {
	to: string;
	children: ReactNode;
	onResultClick?: () => void;
	isKeyboardActive?: boolean;
	resultIndex?: number;
	optionId?: string;
	posInSet?: number;
	setSize?: number;
	resultRef?: (el: HTMLAnchorElement | null) => void;
}

const linkItemStyles = xcss({
	display: 'block',
	paddingBlock: 'space.200',
	paddingInline: 'space.300',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'none',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const linkItemActiveStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
});

const linkItemContentStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	em: {
		fontStyle: 'inherit',
		fontWeight: token('font.weight.bold'),
	},
});

/**
 * __Link item__
 */
export const LinkItem = ({
	to,
	children,
	onResultClick,
	resultRef,
	...props
}: LinkItemProps): jsx.JSX.Element => (
	<CustomItem
		// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
		{...props}
		component={({ children, ...rest }) => (
			<Anchor
				ref={resultRef}
				href={to}
				id={props.optionId}
				role="option"
				tabIndex={-1}
				aria-selected={props.isKeyboardActive}
				aria-posinset={props.posInSet}
				aria-setsize={props.setSize}
				xcss={[linkItemStyles, props.isKeyboardActive && linkItemActiveStyles]}
				{...rest}
				onClick={onResultClick}
			>
				<div css={linkItemContentStyles}>{children}</div>
			</Anchor>
		)}
	>
		{children}
	</CustomItem>
);
